/**
 * Advanced Metrics Calculator
 *
 * Calculates sophisticated capacity metrics like average continuous time
 * and visit capacity.
 *
 * NOTE: These are estimates based on aggregated data. For precise calculations,
 * detailed time-block data would be needed from the backend.
 */

import { ProviderCapacityDay, RoleCapacityDay } from '../types/capacity.types';
import { RoleCapacityDayExtended, VisitConfig } from '../types/metrics.types';

const DEFAULT_VISIT_CONFIG: VisitConfig = {
  durationMinutes: 60,  // Standard visit duration
  avgDriveTimeMinutes: 15, // Average drive time between visits
};

/**
 * Estimate average continuous time blocks for a provider-day
 *
 * This estimates how large the average gap is between visits.
 * For example, if a provider has 180 minutes free across 3 gaps,
 * the average continuous time is 60 minutes.
 *
 * @param providerDay - Provider capacity for a specific day
 * @returns Estimated average continuous minutes
 */
export function estimateAvgContinuousTime(providerDay: ProviderCapacityDay): number {
  const { shiftMinutes, visitMinutes, breakMinutes, remainingMinutes } = providerDay;

  // If no remaining time, return 0
  if (remainingMinutes <= 0) {
    return 0;
  }

  // Estimate number of visit blocks (gaps between visits)
  // Assuming average visit is ~60 minutes, calculate number of visits
  const estimatedVisitCount = Math.ceil(visitMinutes / 60);

  // Number of potential gaps = number of visits + 1 (before first, between, after last)
  // But we only care about usable gaps (not tiny fragments)
  const estimatedGaps = Math.max(1, estimatedVisitCount + 1);

  // Average continuous time = remaining time / number of gaps
  const avgContinuous = remainingMinutes / estimatedGaps;

  return Math.floor(avgContinuous);
}

/**
 * Calculate visit capacity for a provider-day
 *
 * This calculates how many standard visits (with drive time)
 * can fit in the remaining capacity.
 *
 * @param providerDay - Provider capacity for a specific day
 * @param config - Visit configuration (duration and drive time)
 * @returns Number of visits that could fit
 */
export function calculateVisitCapacity(
  providerDay: ProviderCapacityDay,
  config: VisitConfig = DEFAULT_VISIT_CONFIG
): number {
  const { remainingMinutes } = providerDay;

  // If no remaining time, return 0
  if (remainingMinutes <= 0) {
    return 0;
  }

  // Total time needed per visit = visit duration + drive time
  const timePerVisit = config.durationMinutes + config.avgDriveTimeMinutes;

  // Calculate how many visits fit
  const visitCapacity = Math.floor(remainingMinutes / timePerVisit);

  return visitCapacity;
}

/**
 * Calculate extended metrics for a role-day
 *
 * Aggregates provider-level metrics to role level
 *
 * @param roleDay - Base role capacity data
 * @param providers - Provider details for this role-day
 * @param config - Visit configuration
 * @returns Extended role capacity with advanced metrics
 */
export function calculateExtendedRoleMetrics(
  roleDay: RoleCapacityDay,
  providers: ProviderCapacityDay[],
  config: VisitConfig = DEFAULT_VISIT_CONFIG
): RoleCapacityDayExtended {
  // Calculate average continuous time across all providers
  const continuousTimes = providers.map((p) => estimateAvgContinuousTime(p));
  const avgContinuousMinutes =
    continuousTimes.length > 0
      ? Math.floor(continuousTimes.reduce((sum, t) => sum + t, 0) / continuousTimes.length)
      : 0;

  // Calculate total visit capacity across all providers
  const visitCapacities = providers.map((p) => calculateVisitCapacity(p, config));
  const totalVisitCapacity = visitCapacities.reduce((sum, c) => sum + c, 0);

  return {
    ...roleDay,
    avgContinuousMinutes,
    visitCapacity: totalVisitCapacity,
  };
}

/**
 * Build extended role capacity dataset
 *
 * @param roleCapacity - Base role capacity data
 * @param providerCapacity - Provider capacity data for detailed calculations
 * @param config - Visit configuration
 * @returns Extended role capacity array
 */
export function buildExtendedRoleCapacity(
  roleCapacity: RoleCapacityDay[],
  providerCapacity: ProviderCapacityDay[],
  config: VisitConfig = DEFAULT_VISIT_CONFIG
): RoleCapacityDayExtended[] {
  // Group providers by role and date
  const providerMap = new Map<string, ProviderCapacityDay[]>();
  providerCapacity.forEach((provider) => {
    const key = `${provider.role}|${provider.date}`;
    if (!providerMap.has(key)) {
      providerMap.set(key, []);
    }
    providerMap.get(key)!.push(provider);
  });

  // Calculate extended metrics for each role-day
  return roleCapacity.map((roleDay) => {
    const key = `${roleDay.role}|${roleDay.date}`;
    const providers = providerMap.get(key) || [];
    return calculateExtendedRoleMetrics(roleDay, providers, config);
  });
}
